import React, { useCallback, useEffect, useState } from 'react';
import { fetchTaskThreads } from '../../services/assignments';
import { AttachmentList, TaskReplies, formatWhen } from './TaskReplies';
import './LearningTasks.css';

/**
 * Teacher view of each assigned student's private submission conversation and, once grades are finalized,
 * the family's comments on the graded work.
 */
export default function TaskThreadsPanel({ assignment, canReply = true }: any) {
  const [threads, setThreads] = useState<any>([]);
  const [loading, setLoading] = useState<any>(true);
  const [error, setError] = useState<any>('');
  const [selected, setSelected] = useState<any>(null);
  const [channel, setChannel] = useState('student');
  const released = ['graded', 'completed'].includes(assignment.status);

  const load = useCallback(async () => {
    try {
      const rows = await fetchTaskThreads(assignment.id);
      setThreads(rows);
      setSelected((current) => current ?? rows.find((t) => t.replyCount > 0)?.studentId ?? rows[0]?.studentId ?? null);
      setError('');
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [assignment.id]);

  useEffect(() => { load(); }, [load]);

  const needsResponse = (t) => (t.replyCount > 0 && t.lastAuthorRole && t.lastAuthorRole !== 'teacher')
    || (t.familyReplyCount > 0 && t.familyLastAuthorRole && t.familyLastAuthorRole !== 'teacher');
  const selectedThread = threads.find((t) => t.studentId === selected);
  const studentName = selectedThread?.studentName || 'student';
  const activeChannel = released ? channel : 'student';

  return (
    <div className="lt-threads-panel">
      {assignment.attachments?.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <h4 className="lt-thread-title">Shared with students & families</h4>
          <AttachmentList attachments={assignment.attachments} taskId={assignment.id} />
        </div>
      )}
      {loading && <p className="lt-muted" role="status">Loading student replies...</p>}
      {error && <p role="alert" className="lt-error">{error}</p>}
      {!loading && !error && threads.length === 0 && (
        <p className="lt-muted">
          No students can see this task yet. Publish it (not as a draft) to students enrolled in this class to start replies.
        </p>
      )}
      {threads.length > 0 && (
        <div className="lt-threads-layout">
          <ul className="lt-thread-list" aria-label="Student reply threads">
            {threads.map((t) => (
              <li key={t.studentId}>
                <button
                  type="button"
                  className={`lt-thread-item ${selected === t.studentId ? 'active' : ''}`}
                  aria-pressed={selected === t.studentId}
                  onClick={() => setSelected(t.studentId)}
                >
                  <span className="lt-thread-name">{t.studentName}</span>
                  <span className="lt-muted">
                    {t.submitted ? 'Submitted' : 'Not submitted'}
                    {t.replyCount ? ` · ${t.replyCount} message${t.replyCount === 1 ? '' : 's'} · ${formatWhen(t.lastReplyAt)}` : ''}
                  </span>
                  {t.familyReplyCount > 0 && (
                    <span className="lt-muted">
                      Family: {t.familyReplyCount} comment{t.familyReplyCount === 1 ? '' : 's'}
                    </span>
                  )}
                  {needsResponse(t) && <span className="lt-badge">New</span>}
                </button>
              </li>
            ))}
          </ul>
          <div className="lt-thread-detail">
            {selected != null && released && (
              <div className="lt-channel-tabs" role="tablist" aria-label="Conversation">
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeChannel === 'student'}
                  className={`lt-btn ${activeChannel === 'student' ? 'lt-btn-primary' : ''}`}
                  onClick={() => setChannel('student')}
                >
                  Student submission
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeChannel === 'family'}
                  className={`lt-btn ${activeChannel === 'family' ? 'lt-btn-primary' : ''}`}
                  onClick={() => setChannel('family')}
                >
                  Family comments{selectedThread?.familyReplyCount ? ` (${selectedThread.familyReplyCount})` : ''}
                </button>
              </div>
            )}
            {selected != null && (
              <TaskReplies
                key={`${selected}-${activeChannel}`}
                taskId={assignment.id}
                studentId={selected}
                channel={activeChannel}
                canReply={canReply}
                title={activeChannel === 'family'
                  ? `Comments from ${studentName}’s family`
                  : `Submission & conversation with ${studentName}`}
                emptyText={activeChannel === 'family' ? 'No family comments yet.' : 'Nothing submitted yet.'}
                onPosted={load}
              />
            )}
            {selected != null && (
              <p className="lt-muted lt-readonly-note">
                {released
                  ? 'Grades are released. The family can read this student’s submission and send you comments; the student does not see family comments.'
                  : 'Only you and the student see this conversation. Grades and the graded submission are shared with the family after “Finalize & Publish Grades”.'}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
